import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import AdminLayout from '@components/layout/AdminLayout';
import PrivateRoute from '@components/common/PrivateRoute';
import AccessControl from '@components/common/AccessControl';
import ErrorBoundary from '@components/common/ErrorBoundary';
import PageLoader from '@components/common/PageLoader';
import LogoLoader from '@components/common/LogoLoader';

// Lazy-loaded pages for code splitting
// Core pages (loaded immediately after layout)
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const PrivacyPolicyPage = lazy(() => import('@pages/PrivacyPolicy'));
const PublicSupportPage = lazy(() => import('@pages/PublicSupport'));
const AdminDashboard = lazy(() => import('@pages/AdminDashboard/index.jsx'));

// Member Management
const UsersPage = lazy(() => import('@pages/UsersPage/index.jsx'));
const UserDetailPage = lazy(() => import('@pages/UserDetailPage/index.jsx'));
const EmployeeList = lazy(() => import('@pages/EmployeeListPage/index.jsx'));
const EmployeePointsManagement = lazy(() => import('@pages/EmployeePointsPage/index.jsx'));
const MealPointsPage = lazy(() => import('@pages/MealPointsPage'));

// Points & Rewards
const PointsPage = lazy(() => import('@pages/PointsPage/index.jsx'));
const TiersPage = lazy(() => import('@pages/TiersPage/index.jsx'));
const RewardsCatalogPage = lazy(() => import('@pages/RewardsCatalogPage/index.jsx'));
const EarningRulesPage = lazy(() => import('@pages/EarningRulesPage'));
const PointsConfigPage = lazy(() => import('@pages/PointsConfigPage/index.jsx'));

// Engagement & Growth
const GamificationPage = lazy(() => import('@pages/GamificationPage'));
const GamificationConfigPage = lazy(() => import('@pages/GamificationConfigPage/index.jsx'));
const SegmentationPage = lazy(() => import('@pages/SegmentationPage'));
const ReferralsPage = lazy(() => import('@pages/ReferralsPage'));
const OfferManagementPage = lazy(() => import('@pages/OfferManagementPage/index.jsx'));

// Automation & Rules
const RulesEngineWorkflow = lazy(() => import('@pages/RulesEngineWorkflow'));
const WorkflowList = lazy(() => import('@pages/WorkflowList'));
const BroadcastPage = lazy(() => import('@pages/BroadcastPage'));
const CommunicationPage = lazy(() => import('@pages/CommunicationPage'));

// Operations
const BulkOperationsPage = lazy(() => import('@pages/BulkOperationsPage'));
const IntegrationsPage = lazy(() => import('@pages/IntegrationsPage'));
const AntiFraudPage = lazy(() => import('@pages/AntiFraudPage'));
const AdminMenuPage = lazy(() => import('@pages/AdminMenuPage/index.jsx'));
const OrderManagementPage = lazy(() => import('@pages/OrderManagementPage/index.jsx'));
const CouponManagementPage = lazy(() => import('@pages/CouponManagementPage/index.jsx'));
const CarCatalogPage = lazy(() => import('@pages/CarCatalogPage/index.jsx'));
const PaymentTestPage = lazy(() => import('@pages/PaymentTestPage.jsx'));

// Menu & Content
const ProductBranchesPage = lazy(() => import('@pages/ProductBranchesPage'));
const ContentManagement = lazy(() => import('@pages/ContentManagement'));

// Analytics
const AnalyticsPage = lazy(() => import('@pages/AnalyticsPage/index.jsx'));
const ReportsPage = lazy(() => import('@pages/ReportsPage/index.jsx'));
const AuditLogsPage = lazy(() => import('@pages/AuditLogsPage'));
const FraudDetectionPage = lazy(() => import('@pages/FraudDetectionPage'));
const MarnSyncPage = lazy(() => import('@pages/MarnSyncPage'));

// System
const SettingsPage = lazy(() => import('@pages/SettingsPage/index.jsx'));
const ConfigurationPage = lazy(() => import('@pages/ConfigurationPage'));
const CMSPage = lazy(() => import('@pages/CMSPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

// Support
const SupportPage = lazy(() => import('@pages/SupportPage/index.jsx'));
const RatingsReviewsPage = lazy(() => import('@pages/RatingsReviewsPage/index.jsx'));

// Share Link
const ShareLinkPage = lazy(() => import('@pages/ShareLinkPage/index.jsx'));

// Documentation Management
const FaqManagement = lazy(() => import('@pages/DocumentationManagement/FaqManagement'));
const CommonQuestionsConfig = lazy(() => import('@pages/DocumentationManagement/CommonQuestionsConfig'));
const TermsConfig = lazy(() => import('@pages/DocumentationManagement/TermsConfig'));
const VatCertificateConfig = lazy(() => import('@pages/DocumentationManagement/VatCertificateConfig'));

/**
 * Access-controlled route fallback — redirects to dashboard
 */
const AccessDenied = () => <Navigate to="/dashboard" replace />;


/**
 * App Component
 *
 * Main application component with route definitions.
 * Routes are protected by AccessControl using module codes from login API response.
 */
const App = () => {
  return (
    <ErrorBoundary>
      <div className="flex min-h-screen">
        <Suspense fallback={<PageLoader message="Loading application..." />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<LogoLoader message="Loading..." />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <Suspense fallback={<LogoLoader message="Loading..." />}>
                  <PrivacyPolicyPage />
                </Suspense>
              }
            />
            <Route
              path="/help-and-support"
              element={
                <Suspense fallback={<LogoLoader message="Loading..." />}>
                  <PublicSupportPage />
                </Suspense>
              }
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />

              {/* Overview — always accessible */}
              <Route
                path="dashboard"
                element={
                  <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
                    <AdminDashboard />
                  </Suspense>
                }
              />

              <Route
                path="share-link"
                element={
                  <AccessControl moduleCode="ANALYTICS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading share link..." />}>
                      <ShareLinkPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Member Management */}
              <Route
                path="product-catalog"
                element={
                  <AccessControl moduleCode="PRODUCTS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading product catalog..." />}>
                      <AdminMenuPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="lyncs/product-catalog"
                element={
                  <AccessControl moduleCode="LYNCS_PRODUCT_CATALOG" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading product catalog..." />}>
                      <AdminMenuPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="orders"
                element={
                  <AccessControl moduleCode="ORDERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading orders..." />}>
                      <OrderManagementPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="coupons"
                element={
                  <AccessControl moduleCode="ORDERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading coupons..." />}>
                      <CouponManagementPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="payment-test"
                element={
                  <Suspense fallback={<PageLoader message="Loading payment test..." />}>
                    <PaymentTestPage />
                  </Suspense>
                }
              />
              <Route
                path="employees"
                element={
                  <AccessControl moduleCode="EMPLOYEES" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading employees..." />}>
                      <EmployeeList />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="employee-points"
                element={
                  <AccessControl moduleCode="EMPLOYEE_POINTS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading employee points..." />}>
                      <EmployeePointsManagement />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="users"
                element={
                  <AccessControl moduleCode="USERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading users..." />}>
                      <UsersPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="users/:userId"
                element={
                  <AccessControl moduleCode="USERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading user details..." />}>
                      <UserDetailPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="meal-points"
                element={
                  <AccessControl moduleCode="MEAL_POINTS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading meal points..." />}>
                      <MealPointsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="points"
                element={
                  <AccessControl moduleCode="POINTS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading points..." />}>
                      <PointsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="tiers"
                element={
                  <AccessControl moduleCode="TIERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading tiers..." />}>
                      <TiersPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Automation & Rules — no moduleCode, always accessible */}
              <Route
                path="workflows"
                element={
                  <Suspense fallback={<PageLoader message="Loading workflows..." />}>
                    <WorkflowList />
                  </Suspense>
                }
              />
              <Route
                path="rules-engine"
                element={
                  <Suspense fallback={<PageLoader message="Loading rules engine..." />}>
                    <RulesEngineWorkflow />
                  </Suspense>
                }
              />
              <Route
                path="rules"
                element={
                  <Suspense fallback={<PageLoader message="Loading rules..." />}>
                    <RulesEngineWorkflow />
                  </Suspense>
                }
              />

              {/* Rewards & Engagement */}
              <Route
                path="rewards"
                element={
                  <AccessControl moduleCode="REWARDS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading rewards..." />}>
                      <RewardsCatalogPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="gamification"
                element={
                  <AccessControl moduleCode="REWARDS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading gamification..." />}>
                      <GamificationPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="gamification-config"
                element={
                  <AccessControl moduleCode="GAMIFICATION" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading gamification configuration..." />}>
                      <GamificationConfigPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="offers"
                element={
                  <AccessControl moduleCode="OFFERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading offers..." />}>
                      <OfferManagementPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="segmentation"
                element={
                  <AccessControl moduleCode="USERS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading segmentation..." />}>
                      <SegmentationPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="referrals"
                element={
                  <AccessControl moduleCode="REWARDS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading referrals..." />}>
                      <ReferralsPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Marn Admin */}
              <Route
                path="marn-admin/branches"
                element={
                  <AccessControl moduleCode="BRANCHES" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading product branches..." />}>
                      <ProductBranchesPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="lyncs/branches"
                element={
                  <AccessControl moduleCode="LYNCS_PRODUCT_BRANCHES" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading product branches..." />}>
                      <ProductBranchesPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="content-management"
                element={
                  <AccessControl moduleCode="CONTENT_MANAGEMENT" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading content management..." />}>
                      <ContentManagement />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="car-catalog"
                element={
                  <AccessControl moduleCode="CONTENT_MANAGEMENT" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading car catalog..." />}>
                      <CarCatalogPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Operations */}
              <Route
                path="audit-logs"
                element={
                  <AccessControl moduleCode="AUDIT_LOGS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading audit logs..." />}>
                      <AuditLogsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="fraud-detection"
                element={
                  <AccessControl moduleCode="FRAUD" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading fraud detection..." />}>
                      <FraudDetectionPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="marn-sync"
                element={
                  <AccessControl moduleCode="SYNC_LOGS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading marn sync..." />}>
                      <MarnSyncPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="bulk-operations"
                element={
                  <AccessControl moduleCode="CAMPAIGNS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading bulk operations..." />}>
                      <BulkOperationsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="communication"
                element={
                  <AccessControl moduleCode="CAMPAIGNS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading communication..." />}>
                      <CommunicationPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="integrations"
                element={
                  <AccessControl moduleCode="INTEGRATIONS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading integrations..." />}>
                      <IntegrationsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="anti-fraud"
                element={
                  <AccessControl moduleCode="FRAUD" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading anti-fraud..." />}>
                      <AntiFraudPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="earning-rules"
                element={
                  <AccessControl moduleCode="EARNING_RULES" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading earning rules..." />}>
                      <EarningRulesPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="points-config"
                element={
                  <AccessControl moduleCode="SYSTEM_CONFIG" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading points config..." />}>
                      <PointsConfigPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Notifications & Analytics */}
              <Route
                path="broadcast"
                element={
                  <AccessControl moduleCode="NOTIFICATIONS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading broadcast..." />}>
                      <BroadcastPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="analytics"
                element={
                  <AccessControl moduleCode="ANALYTICS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading analytics..." />}>
                      <AnalyticsPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              {/* Downloadable client reports. Shares the ANALYTICS module so no
                  new permission has to be seeded on production. */}
              <Route
                path="reports"
                element={
                  <AccessControl moduleCode="ANALYTICS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading reports..." />}>
                      <ReportsPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Support */}
              <Route
                path="support"
                element={
                  <AccessControl moduleCode="SUPPORT" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading support chat..." />}>
                      <SupportPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="ratings-reviews"
                element={
                  <AccessControl moduleCode="SUPPORT" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading ratings and reviews..." />}>
                      <RatingsReviewsPage />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* Documentation Management */}
              <Route
                path="faq-management"
                element={
                  <AccessControl moduleCode="FAQ" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading FAQ management..." />}>
                      <FaqManagement />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="common-questions-config"
                element={
                  <AccessControl moduleCode="FAQ" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading common questions..." />}>
                      <CommonQuestionsConfig />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="terms-config"
                element={
                  <AccessControl moduleCode="TERMS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading terms configuration..." />}>
                      <TermsConfig />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="vat-certificate"
                element={
                  <AccessControl moduleCode="VAT_CERTIFICATE" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading VAT certificate..." />}>
                      <VatCertificateConfig />
                    </Suspense>
                  </AccessControl>
                }
              />

              {/* System */}
              <Route
                path="cms"
                element={
                  <AccessControl moduleCode="CMS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading CMS..." />}>
                      <CMSPage />
                    </Suspense>
                  </AccessControl>
                }
              />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<PageLoader message="Loading settings..." />}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="configuration"
                element={
                  <AccessControl moduleCode="SETTINGS" fallback={<AccessDenied />}>
                    <Suspense fallback={<PageLoader message="Loading configuration..." />}>
                      <ConfigurationPage />
                    </Suspense>
                  </AccessControl>
                }
              />
            </Route>

            {/* 404 */}
            <Route
              path="*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <NotFoundPage />
                </Suspense>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};

export default App;
